import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Pin, Pencil, Trash2, StickyNote } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { matterNotesApi } from "../../services/api";
import { useUserDataContext } from "../../context/UserDataContext";

interface MatterNotesTabProps {
  matterId: string;
}

interface NoteAuthor {
  _id: string;
  name: string;
  email: string;
}

interface Note {
  _id: string;
  matterId: string;
  authorId: NoteAuthor;
  body: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
}

const getInitials = (name: string) =>
  name
    ?.split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "?";

const MatterNotesTab: React.FC<MatterNotesTabProps> = ({ matterId }) => {
  const { userData } = useUserDataContext();
  const currentUserId = userData._id;

  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [newBody, setNewBody] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");

  const fetchNotes = useCallback(async () => {
    try {
      const res = await matterNotesApi.list(matterId, { limit: 200 });
      setNotes(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch notes:", err);
    } finally {
      setLoading(false);
    }
  }, [matterId]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handlePost = async () => {
    if (!newBody.trim()) return;
    setPosting(true);
    try {
      await matterNotesApi.create({ matterId, body: newBody.trim() });
      setNewBody("");
      fetchNotes();
    } catch (err) {
      console.error("Failed to create note:", err);
    } finally {
      setPosting(false);
    }
  };

  const handleUpdate = async (id: string) => {
    if (!editBody.trim()) return;
    try {
      await matterNotesApi.update(id, { body: editBody.trim() });
      setEditingId(null);
      setEditBody("");
      fetchNotes();
    } catch (err) {
      console.error("Failed to update note:", err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await matterNotesApi.remove(id);
      fetchNotes();
    } catch (err) {
      console.error("Failed to delete note:", err);
    }
  };

  const handleTogglePin = async (id: string) => {
    try {
      await matterNotesApi.togglePin(id);
      fetchNotes();
    } catch (err) {
      console.error("Failed to toggle pin:", err);
    }
  };

  const startEdit = (note: Note) => {
    setEditingId(note._id);
    setEditBody(note.body);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditBody("");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Add a note */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Add a note</h3>
        <textarea
          value={newBody}
          onChange={(e) => setNewBody(e.target.value)}
          placeholder="Write your note here..."
          rows={3}
          className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none transition-colors"
        />
        <div className="flex justify-end mt-3">
          <button
            onClick={handlePost}
            disabled={posting || !newBody.trim()}
            className="text-white rounded-xl px-5 py-2.5 font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: "var(--color-primary)" }}
            onMouseEnter={(e) => { if (!posting) e.currentTarget.style.backgroundColor = "var(--color-primary-hover)"; }}
            onMouseLeave={(e) => { if (!posting) e.currentTarget.style.backgroundColor = "var(--color-primary)"; }}
          >
            {posting ? "Posting..." : "Post Note"}
          </button>
        </div>
      </div>

      {/* Notes feed */}
      {notes.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-16 text-center">
          <StickyNote className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium">No notes yet  -  add the first note</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note, index) => {
            const isAuthor =
              note.authorId?._id === currentUserId;
            const isEditing = editingId === note._id;

            return (
              <motion.div
                key={note._id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, delay: index * 0.04 }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="text-white rounded-full h-8 w-8 flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: "var(--color-accent)" }}>
                      {getInitials(note.authorId?.name || "")}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">
                        {note.authorId?.name || "Unknown"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDistanceToNow(new Date(note.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>
                    {note.isPinned && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        <Pin className="h-3 w-3" />
                        Pinned
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleTogglePin(note._id)}
                      className={`p-1.5 rounded-lg transition-colors ${
                        note.isPinned
                          ? "text-amber-600 hover:bg-amber-50"
                          : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                      }`}
                      title={note.isPinned ? "Unpin" : "Pin"}
                    >
                      <Pin className="h-4 w-4" />
                    </button>
                    {isAuthor && (
                      <>
                        <button
                          onClick={() => startEdit(note)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-colors"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(note._id)}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Body */}
                {isEditing ? (
                  <div>
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={3}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none transition-colors"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                      <button
                        onClick={cancelEdit}
                        className="px-4 py-2 rounded-xl text-sm font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdate(note._id)}
                        disabled={!editBody.trim()}
                        className="text-white rounded-xl px-4 py-2 text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: "var(--color-primary)" }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "var(--color-primary-hover)"}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "var(--color-primary)"}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {note.body}
                  </p>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MatterNotesTab;
